// ###################### CODE VERSION 6 ######################

import { useContext, useEffect, useState, useMemo } from "react";
import AppContext from "../../context/AppContext";
import { http } from "../../api/http";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import SearchIcon from "@mui/icons-material/Search";
import "./Dashboard.css";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

const metaFilters = [
  { label: "Subclasses", value: "Air Cooled Chillers" },
  { label: "Installation Type", value: "Outdoor" },
  { label: "Application", value: "Domestic cooling" },
  { label: "Power Supply", value: "All" },
  { label: "Refrigerant", value: "All" },
  { label: "Fluid", value: "Water" },
//  { label: "Glycol %", value: "0" },
];

const TEMP_LIMITS = {
  inlet: { min: 12, max: 35 },
  outlet: { min: 7, max: 30 },
  ambient: { min: 35, max: 52 },
};

const ParameterRow = ({ label, children }) => (
  <div className="parameter-row">
    <span>{label}</span>
    <div>{children}</div>
  </div>
);

export default function Dashboard() {
  const [seriesError, setSeriesError] = useState(false);
  const [inletTempError, setInletTempError] = useState(false);
  const [outletTempError, setOutletTempError] = useState(false);
  const [ambientTempError, setAmbientTempError] = useState(false);

  const [tempUnit, setTempUnit] = useState("C");
  const [searchQuery, setSearchQuery] = useState("");

  const [inletTempC, setInletTempC] = useState("");
  const [outletTempC, setOutletTempC] = useState("");
  const [ambientTempC, setAmbientTempC] = useState("");

  const [inletDraft, setInletDraft] = useState("");
  const [outletDraft, setOutletDraft] = useState("");
  const [ambientDraft, setAmbientDraft] = useState("");

  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const [selection, setSelection] = useState({
    model: null,
    capacity: null,
    inlet: null,
    outlet: null,
    ambient: null,
  });

  const {
    base_URL,
    seriesList,
    setSeriesList,
    selectedSeries,
    setSelectedSeries,
    setInletTemp,
    setOutletTemp,
    setAmbientTemp,
    coolingCapacity,
    setCoolingCapacity,
  } = useContext(AppContext);

  const navigate = useNavigate();

  const toDraft = (celsius, unit) => {
    if (celsius === "" || celsius === null) return "";
    return unit === "C" ? celsius : ((celsius * 9) / 5 + 32).toFixed(2);
  };

  useEffect(() => {
    setInletDraft(toDraft(inletTempC, tempUnit));
  }, [inletTempC, tempUnit]);

  useEffect(() => {
    setOutletDraft(toDraft(outletTempC, tempUnit));
  }, [outletTempC, tempUnit]);

  useEffect(() => {
    setAmbientDraft(toDraft(ambientTempC, tempUnit));
  }, [ambientTempC, tempUnit]);

  useEffect(() => {
    const fetchSeries = async () => {
      try {
        const data = await http.get("/series");
        const codes = (Array.isArray(data) ? data : []).map((item) => item.series_code);
        // setSeriesList(codes);
        // Demo: show only PW series
        const demoSeriesList = codes.filter((code) => code === "PW");
        setSeriesList(demoSeriesList);
      } catch (error) {
        console.error("Failed to fetch series:", error);
      }
    };

    fetchSeries();
  }, [setSeriesList]);

  const groupedByModel = useMemo(() => {
    const map = {};
    searchResults.forEach((row) => {
      if (!map[row.option_code]) {
        map[row.option_code] = [];
      }
      map[row.option_code].push(row);
    });
    return map;
  }, [searchResults]);

  const models = useMemo(() => Object.keys(groupedByModel), [groupedByModel]);

  const handleModelSelect = (model) => {
    setSelection({
      model,
      capacity: null,
      inlet: null,
      outlet: null,
      ambient: null,
    });
  };

  const activeRows = useMemo(() => {
    return selection.model ? groupedByModel[selection.model] || [] : [];
  }, [selection.model, groupedByModel]);

  const filteredRows = useMemo(() => {
    return activeRows.filter((row) => {
      return (
        (!selection.capacity ||
          row.actual_cooling_capacity === selection.capacity) &&
        (!selection.inlet || row.inlet_temp === selection.inlet) &&
        (!selection.outlet || row.outlet_temp === selection.outlet) &&
        (!selection.ambient || row.ambient_temp === selection.ambient)
      );
    });
  }, [activeRows, selection]);

  useEffect(() => {
    if (filteredRows.length !== 1) return;

    const row = filteredRows[0];

    setSelection((prev) => {
      const next = {
        ...prev,
        capacity: prev.capacity ?? row.actual_cooling_capacity,
        inlet: prev.inlet ?? row.inlet_temp,
        outlet: prev.outlet ?? row.outlet_temp,
        ambient: prev.ambient ?? row.ambient_temp,
      };

      // 🔒 Prevent infinite loop
      const hasChanged =
        next.capacity !== prev.capacity ||
        next.inlet !== prev.inlet ||
        next.outlet !== prev.outlet ||
        next.ambient !== prev.ambient;

      return hasChanged ? next : prev;
    });
  }, [filteredRows]);

  const isResolved = filteredRows.length === 1;

  const uniqueValues = (rows, key) => [...new Set(rows.map((r) => r[key]))];

  const capacityOptions = uniqueValues(filteredRows, "actual_cooling_capacity");
  const inletOptions = uniqueValues(filteredRows, "inlet_temp");
  const outletOptions = uniqueValues(filteredRows, "outlet_temp");
  const ambientOptions = uniqueValues(filteredRows, "ambient_temp");

  useEffect(() => {
    if (models.length === 1 && !selection.model) {
      setSelection((prev) => ({
        ...prev,
        model: models[0],
      }));
    }
  }, [models, selection.model]);

  const handleParamChange = (key) => (e) => {
    const value = e.target.value || null;

    setSelection((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleResetConfig = () => {
    setSelection((prev) => ({
      ...prev,
      capacity: null,
      inlet: null,
      outlet: null,
      ambient: null,
    }));
  };

  const handleSeriesToggle = (seriesCode) => {
    setSeriesError(false);
    setSelectedSeries((prev) => {
      if (prev.includes(seriesCode)) {
        return prev.filter((code) => code !== seriesCode);
      }
      return [...prev, seriesCode];
    });
  };

  const fetchChillersBySearchTerm = async (searchTerm) => {
    try {
      setSearchLoading(true);
      const body = await http.get(`/chillers/by/${searchTerm}`);
      setSearchResults(body.data || []);
    } catch (error) {
      console.error("Search error:", error);
      toast.error("No chillers found for this series");
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchProducts = () => {
    if (!searchQuery.trim()) {
      toast.warning("Please enter a search term");
      return;
    }

    setSelection({
      model: null,
      capacity: null,
      inlet: null,
      outlet: null,
      ambient: null,
    });

    setIsSearchModalOpen(true);
    fetchChillersBySearchTerm(searchQuery.trim().toUpperCase());
  };

  const handleCloseSearchModal = () => {
    setIsSearchModalOpen(false);
    setSearchQuery(""); // ✅ clear input
  };

  const applyTemperaturesFromSearch = (row) => {
    const inletC = row.inlet_temp;
    const outletC = row.outlet_temp;
    const ambientC = row.ambient_temp;
    const capacity = row.actual_cooling_capacity;

    // 1️⃣ Update canonical (Celsius) state
    setInletTempC(inletC);
    setOutletTempC(outletC);
    setAmbientTempC(ambientC);

    // 2️⃣ Sync to context (always Celsius)
    setInletTemp(inletC);
    setOutletTemp(outletC);
    setAmbientTemp(ambientC);

    // 3️⃣ Sync cooling capacity to context
  setCoolingCapacity(capacity);
  };

  const handleSubmitSearchSelection = () => {
    if (filteredRows.length !== 1) {
      toast.warning("Please complete the configuration");
      return;
    }

    const resolvedRow = filteredRows[0];

    // 1. Auto-select series
    setSelectedSeries([resolvedRow.series_code]);

    // 2. Apply temperatures from resolved configuration
    applyTemperaturesFromSearch(resolvedRow);

    // 3. Close dialog & cleanup
    setIsSearchModalOpen(false);
    setSearchQuery("");

    // 4. Reset configurator state
    setSelection({
      model: null,
      capacity: null,
      inlet: null,
      outlet: null,
      ambient: null,
    });

    // 5. Navigate
    navigate("/result-screen");
  };

  const isWithinRange = (value, { min, max }) => {
    if (value === "" || value === null) return false;
    if (typeof value !== "number" || Number.isNaN(value)) return false;
    return value >= min && value <= max;
  };

  const handleSelectAll = () => {
    setInletTempError(false);
    setOutletTempError(false);
    setAmbientTempError(false);

    if (!selectedSeries.length) {
      toast.warning("Please select at least one series to continue.");
      setSeriesError(true);
      return;
    }

    // 🔑 FORCE COMMIT DRAFTS
    commitTemperature(inletDraft, setInletTempC);
    commitTemperature(outletDraft, setOutletTempC);
    commitTemperature(ambientDraft, setAmbientTempC);

    let hasErrors = false;
    let hasEmpty = false;

    // -------- REQUIRED CHECK --------
    if (inletTempC === "" || inletTempC === null) {
      setInletTempError(true);
      hasErrors = true;
      hasEmpty = true;
    }

    if (outletTempC === "" || outletTempC === null) {
      setOutletTempError(true);
      hasErrors = true;
      hasEmpty = true;
    }

    if (ambientTempC === "" || ambientTempC === null) {
      setAmbientTempError(true);
      hasErrors = true;
      hasEmpty = true;
    }

    if (hasEmpty) {
      toast.warning("Please enter the required temperatures.");
      return;
    }

    // -------- RANGE CHECK --------
    if (!isWithinRange(inletTempC, TEMP_LIMITS.inlet)) {
      setInletTempError(true);
      hasErrors = true;
    }

    if (!isWithinRange(outletTempC, TEMP_LIMITS.outlet)) {
      setOutletTempError(true);
      hasErrors = true;
    }

    if (!isWithinRange(ambientTempC, TEMP_LIMITS.ambient)) {
      setAmbientTempError(true);
      hasErrors = true;
    }

    if (hasErrors) {
      toast.warning(
        "Temperature values are out of allowed range. Please review."
      );
      return;
    }

    // ✅ SYNC TO CONTEXT (always Celsius)
    setInletTemp(inletTempC);
    setOutletTemp(outletTempC);
    setAmbientTemp(ambientTempC);

    navigate("/result-screen");
  };

  const commitTemperature = (draft, setCelsius) => {
    if (draft === "") {
      setCelsius("");
      return;
    }

    const num = Number(draft);
    if (Number.isNaN(num)) return;

    const celsius = tempUnit === "C" ? num : ((num - 32) * 5) / 9;

    setCelsius(Number(celsius.toFixed(2)));
  };

  const formatLimit = (c) =>
    tempUnit === "C" ? c : Math.round((c * 9) / 5 + 32);

  return (
    <div className="dashboard">
      <header className="dashboard__filters">
        <div className="dashboard__filters-left">
          <Button
            variant="outlined"
            disableElevation
            onClick={() => navigate("/")}
            sx={{
              minWidth: 44,
              height: 40,
              border: "none",
              borderRadius: "16px",
              color: "#0f172a",
              fontWeight: 700,
            }}
          >
            <ArrowBackIosNewIcon sx={{ fontSize: 20, color: "#0f172a" }} />
          </Button>
          <div>
            <p>User</p>
            <h1>Configurator</h1>
          </div>
        </div>

        <div className="dashboard__search-section">
          <div className="search-input-wrapper">
            <SearchIcon sx={{ fontSize: 20, color: "#64748b" }} />
            <input
              type="text"
              className="search-input"
              placeholder="Search Chiller..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearchProducts();
              }}
            />
          </div>
          <button className="search-button" onClick={handleSearchProducts}>
            Search Products
          </button>
        </div>
      </header>

      {/* Dialog for searching chiller */}
      <Dialog
        open={isSearchModalOpen}
        onClose={handleCloseSearchModal}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: "20px",
          },
        }}
      >
        {/* ---------- HEADER ---------- */}
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: 20,
            color: "#0f172a",
            pb: 1,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Box>
            Search Results
            <p
              style={{
                marginTop: 6,
                marginBottom: 0,
                fontSize: 14,
                fontWeight: 400,
                color: "#64748b",
              }}
            >
              Showing results related to "<strong>{searchQuery}</strong>".
              Select a model to continue.
            </p>
          </Box>
          <Box>
            <Button
              variant="outlined"
              color="error"
              onClick={handleCloseSearchModal}
              sx={{
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Close
            </Button>
          </Box>
        </DialogTitle>

        {/* ---------- CONTENT ---------- */}
        <DialogContent dividers sx={{ minHeight: 260, padding: "16px 20px" }}>
          {/* ---------------- LOADING STATE ---------------- */}
          {searchLoading && (
            <p
              style={{
                fontSize: 14,
                color: "#64748b",
                textAlign: "center",
                marginTop: 40,
              }}
            >
              Loading chillers…
            </p>
          )}

          {/* ---------------- EMPTY STATE ---------------- */}
          {!searchLoading && searchResults.length === 0 && (
            <p
              style={{
                fontSize: 14,
                color: "#94a3b8",
                textAlign: "center",
                marginTop: 40,
              }}
            >
              No chillers found for this series.
            </p>
          )}

          {/* ---------------- CONFIGURATOR TABLE ---------------- */}
          {!searchLoading && searchResults.length > 0 && (
            <div className="config-table">
              {/* Header */}
              <div className="config-table__header">
                <span />
                <span>Model</span>
                <span>Cooling Capacity</span>
                <span>Inlet (°C)</span>
                <span>Outlet (°C)</span>
                <span>Ambient (°C)</span>
              </div>

              {/* Body */}
              <div className="config-table__body">
                {models.map((model) => {
                  const isActiveModel = selection.model === model;

                  return (
                    <div
                      key={model}
                      className={`config-table__row ${
                        isActiveModel ? "active" : ""
                      }`}
                    >
                      {/* Model radio */}
                      <input
                        type="radio"
                        name="model"
                        checked={isActiveModel}
                        onChange={() => handleModelSelect(model)}
                      />

                      {/* Model name */}
                      <strong>{model}</strong>

                      {/* Cooling Capacity */}
                      <select
                        disabled={!isActiveModel}
                        value={selection.capacity ?? ""}
                        onChange={handleParamChange("capacity")}
                      >
                        <option value="">Select</option>
                        {isActiveModel &&
                          capacityOptions.map((v) => (
                            <option key={v} value={v}>
                              {v}
                            </option>
                          ))}
                      </select>

                      {/* Inlet */}
                      <select
                        disabled={!isActiveModel}
                        value={selection.inlet ?? ""}
                        onChange={handleParamChange("inlet")}
                      >
                        <option value="">Select</option>
                        {isActiveModel &&
                          inletOptions.map((v) => (
                            <option key={v} value={v}>
                              {v}
                            </option>
                          ))}
                      </select>

                      {/* Outlet */}
                      <select
                        disabled={!isActiveModel}
                        value={selection.outlet ?? ""}
                        onChange={handleParamChange("outlet")}
                      >
                        <option value="">Select</option>
                        {isActiveModel &&
                          outletOptions.map((v) => (
                            <option key={v} value={v}>
                              {v}
                            </option>
                          ))}
                      </select>

                      {/* Ambient */}
                      <select
                        disabled={!isActiveModel}
                        value={selection.ambient ?? ""}
                        onChange={handleParamChange("ambient")}
                      >
                        <option value="">Select</option>
                        {isActiveModel &&
                          ambientOptions.map((v) => (
                            <option key={v} value={v}>
                              {v}
                            </option>
                          ))}
                      </select>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </DialogContent>

        {/* ---------- FOOTER ---------- */}
        <DialogActions
          sx={{
            padding: "16px 20px",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Box>
            {isResolved && (
              <span
                style={{
                  marginLeft: 10,
                  fontSize: 14,
                  color: "#16a34a",
                  fontWeight: 600,
                }}
              >
                ✔ Configuration resolved
              </span>
            )}
          </Box>

          <Box sx={{ display: "flex", gap: "8px" }}>
            <Button
              variant="outlined"
              className="dialog-secondary-button"
              sx={{
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 600,
              }}
              disabled={!selection.model}
              onClick={handleResetConfig}
            >
              Reset
            </Button>

            <Button
              variant="contained"
              className="dialog-primary-button"
              sx={{
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 600,
              }}
              disabled={filteredRows.length !== 1}
              onClick={handleSubmitSearchSelection}
            >
              Submit
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <div className="dashboard__meta-filters">
        <div className="filter-chip-group">
          {metaFilters.map((item) => (
            <div className="chip-field" key={item.label}>
              <label>{item.label}</label>
              {item.label.includes("%") || item.label.includes("Factor") ? (
                <input type="number" placeholder={item.value} />
              ) : (
                <select>
                  <option>{item.value}</option>
                </select>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="dashboard__grid">
        <section className={`panel panel--series`}>
          <div className="panel__header">
            <h2>Series</h2>
            <p>Choose one or multiple items from the list</p>
          </div>

          <div className={`series-table ${seriesError ? "error-glow" : ""}`}>
            <div className="series-table__head">
              <span>Series</span>
              {/* <span>Version</span> */}
              {/* <span>Option</span> */}
            </div>

            <div className="series-table__body">
              {seriesList.map((model) => (
                <label className="series-row" key={model}>
                  <div className="series-row__main">
                    <input
                      type="checkbox"
                      checked={selectedSeries.includes(model)}
                      onChange={() => handleSeriesToggle(model)}
                    />
                    <span>{model}</span>
                  </div>
                  {/* <span className="muted">—</span> */}
                  {/* <span className="muted">—</span> */}
                </label>
              ))}
            </div>
          </div>
        </section>

        <section className="panel panel--parameters">
          <div
            className={`param-section ${
              inletTempError || outletTempError ? "error-glow" : ""
            }`}
          >
            <h3>Chilled Water Temperature</h3>

            <ParameterRow label="Chiller Inlet Temperature">
              <div className="single-temp-input">
                <input
                  type="number"
                  className="temp-input"
                  placeholder={`${formatLimit(12)}–${formatLimit(35)}`}
                  value={inletDraft}
                  onChange={(e) => setInletDraft(e.target.value)}
                  onBlur={() => commitTemperature(inletDraft, setInletTempC)}
                />

                <select
                  value={tempUnit}
                  onChange={(e) => setTempUnit(e.target.value)}
                >
                  <option value="C">°C</option>
                  <option value="F">°F</option>
                </select>
              </div>
            </ParameterRow>

            <ParameterRow label="Chiller Outlet Temperature">
              <div className="single-temp-input">
                <input
                  type="number"
                  className="temp-input"
                  placeholder={`${formatLimit(7)}–${formatLimit(30)}`}
                  value={outletDraft}
                  onChange={(e) => setOutletDraft(e.target.value)}
                  onBlur={() => commitTemperature(outletDraft, setOutletTempC)}
                />

                <select
                  value={tempUnit}
                  onChange={(e) => setTempUnit(e.target.value)}
                >
                  <option value="C">°C</option>
                  <option value="F">°F</option>
                </select>
              </div>
            </ParameterRow>
          </div>

          <div
            className={`param-section ${ambientTempError ? "error-glow" : ""}`}
          >
            <h3>Outdoor Condition</h3>

            <ParameterRow label="Ambient Temperature">
              <div className="single-temp-input">
                <input
                  type="number"
                  className="temp-input"
                  placeholder={`${formatLimit(35)}–${formatLimit(52)}`}
                  value={ambientDraft}
                  onChange={(e) => setAmbientDraft(e.target.value)}
                  onBlur={() =>
                    commitTemperature(ambientDraft, setAmbientTempC)
                  }
                />

                <select
                  value={tempUnit}
                  onChange={(e) => setTempUnit(e.target.value)}
                >
                  <option value="C">°C</option>
                  <option value="F">°F</option>
                </select>
              </div>
            </ParameterRow>
          </div>

          <div className="param-section">
            <h3>Other Parameters</h3>

            <ParameterRow label="Type">
              <div className="temp-input-group">
                <select className="parameter-select">
                  <option>Cooling Capacity</option>
                </select>
              </div>
            </ParameterRow>

            <ParameterRow label="Capacity">
              <div className="dual-temp-inputs">
                <div className="temp-input-group">
                  <input
                    type="number"
                    className="temp-input"
                    placeholder="Enter capacity"
                    value={coolingCapacity ?? ""}
                    onChange={(e) =>
                      setCoolingCapacity(
                        e.target.value === "" ? null : Number(e.target.value)
                      )
                    }
                  />
                  <span className="temp-unit">kW</span>
                </div>
                {/* <div className="temp-input-group">
                  <input
                    type="number"
                    className="temp-input"
                    defaultValue="20"
                    placeholder="Enter tolerance"
                    aria-label="Capacity tolerance percent"
                  />
                  <span className="temp-unit">± [%]</span>
                </div> */}
              </div>
            </ParameterRow>
          </div>
        </section>
      </div>

      <footer className="dashboard__footer">
        <button className="primary" onClick={handleSelectAll}>
          Select All Models
        </button>
        <button className="outline">Eurovent Selection</button>
      </footer>
    </div>
  );
}

// ###################### CODE VERSION 5 ######################
// this code supports search functionality with all the data in dialog box in individual row for different operating ranges. TOTAL ROWS 48

// import { useContext, useEffect, useState } from "react";
// import AppContext from "../../context/AppContext";
// import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
// import SearchIcon from "@mui/icons-material/Search";
// import "./Dashboard.css";
// import { useNavigate } from "react-router-dom";
// import { toast } from "react-toastify";
// import {
//   Button,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
// } from "@mui/material";
// import TempToggle from "../../components/TempToggle/TempToggle";

// const metaFilters = [
//   { label: "Subclasses", value: "Air Cooled Chillers" },
//   { label: "Installation Type", value: "Outdoor" },
//   { label: "Application", value: "Domestic cooling" },
//   { label: "Power Supply", value: "All" },
//   { label: "Refrigerant", value: "All" },
//   { label: "Fluid", value: "Water" },
//   { label: "Glycol %", value: "0" },
// ];

// // Temperature conversion functions
// const celsiusToFahrenheit = (celsius) => {
//   if (!celsius && celsius !== 0) return "";
//   return ((parseFloat(celsius) * 9) / 5 + 32).toFixed(2);
// };

// const fahrenheitToCelsius = (fahrenheit) => {
//   if (!fahrenheit && fahrenheit !== 0) return "";
//   return (((parseFloat(fahrenheit) - 32) * 5) / 9).toFixed(2);
// };

// const ParameterRow = ({ label, children }) => (
//   <div className="parameter-row">
//     <span>{label}</span>
//     <div>{children}</div>
//   </div>
// );

// export default function Dashboard() {
//   const [seriesError, setSeriesError] = useState(false);
//   const [inletTempError, setInletTempError] = useState(false);
//   const [outletTempError, setOutletTempError] = useState(false);
//   const [ambientTempError, setAmbientTempError] = useState(false);

//   const [tempUnit, setTempUnit] = useState("C");
//   const [searchQuery, setSearchQuery] = useState("");

//   // Separate states for inlet temperature in C and F
//   const [inletTempC, setInletTempC] = useState("");
//   const [inletTempF, setInletTempF] = useState("");

//   // Separate states for outlet temperature in C and F
//   const [outletTempC, setOutletTempC] = useState("");
//   const [outletTempF, setOutletTempF] = useState("");

//   // Separate states for ambient temperature in C and F
//   const [ambientTempC, setAmbientTempC] = useState("");
//   const [ambientTempF, setAmbientTempF] = useState("");

//   const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
//   const [searchResults, setSearchResults] = useState([]);
//   const [searchLoading, setSearchLoading] = useState(false);
//   const [selectedSearchRow, setSelectedSearchRow] = useState(null);

//   const {
//     base_URL,
//     seriesList,
//     setSeriesList,
//     selectedSeries,
//     setSelectedSeries,
//     setInletTemp,
//     setOutletTemp,
//     setAmbientTemp,
//   } = useContext(AppContext);

//   const navigate = useNavigate();

//   useEffect(() => {
//     const fetchSeries = async () => {
//       try {
//         const res = await fetch(`${base_URL}/api/series`);
//         const data = await res.json();
//         const codes = data.map((item) => item.series_code);
//         setSeriesList(codes);
//       } catch (error) {
//         console.error("Failed to fetch series:", error);
//       }
//     };

//     fetchSeries();
//   }, [base_URL, setSeriesList]);

//   const handleSeriesToggle = (seriesCode) => {
//     setSeriesError(false);
//     setSelectedSeries((prev) => {
//       if (prev.includes(seriesCode)) {
//         return prev.filter((code) => code !== seriesCode);
//       }
//       return [...prev, seriesCode];
//     });
//   };

//   const fetchChillersBySearchTerm = async (searchTerm) => {
//     try {
//       setSearchLoading(true);

//       const res = await fetch(`${base_URL}/api/chillers/by/${searchTerm}`);

//       const body = await res.json();

//       if (!res.ok) {
//         throw new Error(body?.error || "Failed to fetch chillers");
//       }

//       setSearchResults(body.data || []);
//     } catch (error) {
//       console.error("Search error:", error);
//       toast.error("No chillers found for this series");
//       setSearchResults([]);
//     } finally {
//       setSearchLoading(false);
//     }
//   };

//   const handleSearchProducts = () => {
//     if (!searchQuery.trim()) {
//       toast.warning("Please enter a search term");
//       return;
//     }

//     setIsSearchModalOpen(true);
//     fetchChillersBySearchTerm(searchQuery.trim().toUpperCase());
//   };

//   const handleCloseSearchModal = () => {
//     setIsSearchModalOpen(false);
//     setSearchQuery(""); // ✅ clear input
//     setSearchResults([]);
//   };

//   const applyTemperaturesFromSearch = (row) => {
//     const inletC = row.inlet_temp;
//     const outletC = row.outlet_temp;
//     const ambientC = row.ambient_temp;

//     if (tempUnit === "C") {
//       setInletTempC(inletC);
//       setOutletTempC(outletC);
//       setAmbientTempC(ambientC);

//       setInletTempF(celsiusToFahrenheit(inletC));
//       setOutletTempF(celsiusToFahrenheit(outletC));
//       setAmbientTempF(celsiusToFahrenheit(ambientC));

//       setInletTemp(inletC);
//       setOutletTemp(outletC);
//       setAmbientTemp(ambientC);
//     } else {
//       const inletF = celsiusToFahrenheit(inletC);
//       const outletF = celsiusToFahrenheit(outletC);
//       const ambientF = celsiusToFahrenheit(ambientC);

//       setInletTempF(inletF);
//       setOutletTempF(outletF);
//       setAmbientTempF(ambientF);

//       setInletTempC(inletC);
//       setOutletTempC(outletC);
//       setAmbientTempC(ambientC);

//       setInletTemp(inletC);
//       setOutletTemp(outletC);
//       setAmbientTemp(ambientC);
//     }
//   };

//   const handleSubmitSearchSelection = () => {
//     if (!selectedSearchRow) {
//       toast.warning("Please select a chiller to continue");
//       return;
//     }

//     // 1. Auto-select series
//     // setSelectedSeries([searchQuery.toUpperCase()]);
//     setSelectedSeries([selectedSearchRow.series_code]);

//     // 2. Apply temperatures
//     applyTemperaturesFromSearch(selectedSearchRow);

//     // 3. Close dialog
//     setIsSearchModalOpen(false);
//     setSearchQuery("");
//     setSelectedSearchRow(null);

//     // 4. Navigate
//     // navigate("/result-screen");
//   };

//   const handleSelectAll = () => {
//     setInletTempError(false);
//     setOutletTempError(false);
//     setAmbientTempError(false);

//     if (!selectedSeries.length) {
//       toast.warning("Please select at least one series to continue.");
//       setSeriesError(true);
//       return;
//     }

//     const activeInletTemp = tempUnit === "C" ? inletTempC : inletTempF;
//     const activeOutletTemp = tempUnit === "C" ? outletTempC : outletTempF;
//     const activeAmbientTemp = tempUnit === "C" ? ambientTempC : ambientTempF;

//     let hasErrors = false;

//     if (!activeInletTemp) {
//       setInletTempError(true);
//       hasErrors = true;
//     }
//     if (!activeOutletTemp) {
//       setOutletTempError(true);
//       hasErrors = true;
//     }
//     if (!ambientTempC && !ambientTempF) {
//       setAmbientTempError(true);
//       hasErrors = true;
//     }

//     if (hasErrors) {
//       toast.warning("Please enter all the Temperatures.");
//       return;
//     }

//     // ✅ SYNC TO CONTEXT (THIS WAS MISSING)
//     setInletTemp(activeInletTemp);
//     setOutletTemp(activeOutletTemp);
//     setAmbientTemp(activeAmbientTemp);

//     navigate("/result-screen");
//   };

//   // Handlers for Inlet Temperature
//   // const handleInletTempCChange = (e) => {
//   //   setInletTempError(false);
//   //   const value = e.target.value;
//   //   setInletTempC(value);
//   //   setInletTempF(celsiusToFahrenheit(value));
//   //   setInletTemp(value);
//   // };

//   // const handleInletTempFChange = (e) => {
//   //   setInletTempError(false);
//   //   const value = e.target.value;
//   //   setInletTempF(value);
//   //   setInletTempC(fahrenheitToCelsius(value));
//   //   setInletTemp(fahrenheitToCelsius(value));
//   // };

//   // // Handlers for Outlet Temperature
//   // const handleOutletTempCChange = (e) => {
//   //   setOutletTempError(false);
//   //   const value = e.target.value;
//   //   setOutletTempC(value);
//   //   setOutletTempF(celsiusToFahrenheit(value));
//   //   setOutletTemp(value);
//   // };

//   // const handleOutletTempFChange = (e) => {
//   //   setOutletTempError(false);
//   //   const value = e.target.value;
//   //   setOutletTempF(value);
//   //   setOutletTempC(fahrenheitToCelsius(value));
//   //   setOutletTemp(fahrenheitToCelsius(value));
//   // };

//   // // Handlers for Ambient Temperature
//   // const handleAmbientTempCChange = (e) => {
//   //   setAmbientTempError(false);
//   //   const value = e.target.value;
//   //   setAmbientTempC(value);
//   //   setAmbientTempF(celsiusToFahrenheit(value));
//   //   setAmbientTemp(value);
//   // };

//   // const handleAmbientTempFChange = (e) => {
//   //   setAmbientTempError(false);
//   //   const value = e.target.value;
//   //   setAmbientTempF(value);
//   //   setAmbientTempC(fahrenheitToCelsius(value));
//   //   setAmbientTemp(fahrenheitToCelsius(value));
//   // };

//   /* ---------------- Temperature change ---------------- */
//   const handleTempChange = (setter) => (e) => {
//     const v = e.target.value;
//     if (v === "") return setter("");
//     setter(tempUnit === "C" ? v : fahrenheitToCelsius(v));
//   };

//   const displayTemp = (valueC) =>
//     tempUnit === "C" ? valueC : celsiusToFahrenheit(valueC);

//   return (
//     <div className="dashboard">
//       <header className="dashboard__filters">
//         <div className="dashboard__filters-left">
//           <Button
//             variant="outlined"
//             disableElevation
//             onClick={() => navigate(-1)}
//             sx={{
//               minWidth: 44,
//               height: 40,
//               border: "none",
//               borderRadius: "16px",
//               color: "#0f172a",
//               fontWeight: 700,
//             }}
//           >
//             <ArrowBackIosNewIcon sx={{ fontSize: 20, color: "#0f172a" }} />
//           </Button>
//           <div>
//             <p>User</p>
//             <h1>Configurator</h1>
//           </div>
//         </div>

//         <div className="dashboard__search-section">
//           <div className="search-input-wrapper">
//             <SearchIcon sx={{ fontSize: 20, color: "#64748b" }} />
//             <input
//               type="text"
//               className="search-input"
//               placeholder="Search Chiller..."
//               value={searchQuery}
//               onChange={(e) => setSearchQuery(e.target.value)}
//               onKeyPress={(e) => e.key === "Enter" && handleSearchProducts()}
//             />
//           </div>
//           <button className="search-button" onClick={handleSearchProducts}>
//             Search Products
//           </button>
//         </div>
//       </header>

//       {/* Dialog for searching chiller */}
//       <Dialog
//         open={isSearchModalOpen}
//         onClose={handleCloseSearchModal}
//         fullWidth
//         maxWidth="md"
//         PaperProps={{
//           sx: {
//             borderRadius: "20px",
//             // padding: "10px",
//           },
//         }}
//       >
//         {/* ---------- HEADER ---------- */}
//         <DialogTitle
//           sx={{
//             fontWeight: 700,
//             fontSize: 20,
//             color: "#0f172a",
//             pb: 1,
//           }}
//         >
//           Search Results
//           <p
//             style={{
//               marginTop: 6,
//               fontSize: 14,
//               fontWeight: 400,
//               color: "#64748b",
//             }}
//           >
//             Showing results related to "<strong>{searchQuery}</strong>". Select
//             a model to continue.
//           </p>
//         </DialogTitle>

//         {/* ---------- CONTENT ---------- */}
//         <DialogContent dividers sx={{ minHeight: 260, padding: "16px 20px" }}>
//           {/* ---------------- LOADING STATE ---------------- */}
//           {searchLoading && (
//             <p
//               style={{
//                 fontSize: 14,
//                 color: "#64748b",
//                 textAlign: "center",
//                 marginTop: 40,
//               }}
//             >
//               Loading chillers…
//             </p>
//           )}

//           {/* ---------------- EMPTY STATE ---------------- */}
//           {!searchLoading && searchResults.length === 0 && (
//             <p
//               style={{
//                 fontSize: 14,
//                 color: "#94a3b8",
//                 textAlign: "center",
//                 marginTop: 40,
//               }}
//             >
//               No chillers found for this series.
//             </p>
//           )}

//           {/* ---------------- RESULTS TABLE ---------------- */}
//           {!searchLoading && searchResults.length > 0 && (
//             <div className="search-table">
//               {/* Header */}
//               <div className="search-table__header">
//                 <span>Model</span>
//                 <span style={{ textAlign: "center" }}>
//                   Cooling Capacity (kW)
//                 </span>
//                 <span style={{ textAlign: "center" }}>Inlet (°C)</span>
//                 <span style={{ textAlign: "center" }}>Outlet (°C)</span>
//                 <span style={{ textAlign: "center" }}>Ambient (°C)</span>
//               </div>

//               {/* Scrollable Body */}
//               <div className="search-table__body">
//                 {searchResults.map((row) => {
//                   const isSelected = selectedSearchRow?.row_id === row.row_id;

//                   return (
//                     <div
//                       key={row.row_id}
//                       onClick={() => setSelectedSearchRow(row)}
//                       className={`search-table__row ${
//                         isSelected ? "selected" : ""
//                       }`}
//                     >
//                       <strong>{row.model}</strong>
//                       <span>{row.actual_cooling_capacity}</span>
//                       <span>{row.inlet_temp}</span>
//                       <span>{row.outlet_temp}</span>
//                       <span>{row.ambient_temp}</span>
//                     </div>
//                   );
//                 })}
//               </div>
//             </div>
//           )}
//         </DialogContent>

//         {/* ---------- FOOTER ---------- */}
//         <DialogActions
//           sx={{
//             padding: "16px 20px",
//             display: "flex",
//             justifyContent: "flex-end",
//             gap: "2px",
//           }}
//         >
//           <Button
//             variant="outlined"
//             className="dialog-secondary-button"
//             onClick={handleCloseSearchModal}
//             sx={{
//               borderRadius: "12px",
//               textTransform: "none",
//               fontWeight: 600,
//             }}
//           >
//             Close
//           </Button>

//           <Button
//             variant="contained"
//             className="dialog-primary-button"
//             sx={{
//               borderRadius: "12px",
//               textTransform: "none",
//               fontWeight: 600,
//             }}
//             disabled={!selectedSearchRow}
//             onClick={handleSubmitSearchSelection}
//           >
//             Submit
//           </Button>
//         </DialogActions>
//       </Dialog>

//       <div className="dashboard__meta-filters">
//         <div className="filter-chip-group">
//           {metaFilters.map((item) => (
//             <div className="chip-field" key={item.label}>
//               <label>{item.label}</label>
//               {item.label.includes("%") || item.label.includes("Factor") ? (
//                 <input type="number" placeholder={item.value} />
//               ) : (
//                 <select>
//                   <option>{item.value}</option>
//                 </select>
//               )}
//             </div>
//           ))}
//         </div>
//       </div>

//       <div className="dashboard__grid">
//         <section className={`panel panel--series`}>
//           <div className="panel__header">
//             <h2>Series</h2>
//             <p>Choose one or multiple items from the list</p>
//           </div>

//           <div className={`series-table ${seriesError ? "error-glow" : ""}`}>
//             <div className="series-table__head">
//               <span>Series</span>
//               {/* <span>Version</span>
//               <span>Option</span> */}
//             </div>

//             <div className="series-table__body">
//               {seriesList.map((model) => (
//                 <label className="series-row" key={model}>
//                   <div className="series-row__main">
//                     <input
//                       type="checkbox"
//                       checked={selectedSeries.includes(model)}
//                       onChange={() => handleSeriesToggle(model)}
//                     />
//                     <span>{model}</span>
//                   </div>
//                   {/* <span className="muted">—</span>
//                   <span className="muted">—</span> */}
//                 </label>
//               ))}
//             </div>
//           </div>
//         </section>

//         <section className="panel panel--parameters">
//           {/* <div
//             style={{
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "end",
//             }}
//           >
//             <TempToggle value={tempUnit} onChange={setTempUnit} />
//           </div> */}

//           <div
//             className={`param-section ${
//               inletTempError || outletTempError ? "error-glow" : ""
//             }`}
//           >
//             <h3>Chilled Water Temperature</h3>

//             <ParameterRow label="Chiller Inlet Temperature">
//               <div className="single-temp-input">
//                 <input
//                   type="number"
//                   className="temp-input"
//                   value={displayTemp(inletTempC)}
//                   onChange={handleTempChange(setInletTempC)}
//                 />
//                 <select
//                   value={tempUnit}
//                   onChange={(e) => setTempUnit(e.target.value)}
//                 >
//                   <option value="C">°C</option>
//                   <option value="F">°F</option>
//                 </select>
//               </div>
//             </ParameterRow>

//             <ParameterRow label="Chiller Outlet Temperature">
//               <div className="single-temp-input">
//                 <input
//                   type="number"
//                   className="temp-input"
//                   value={displayTemp(outletTempC)}
//                   onChange={handleTempChange(setOutletTempC)}
//                 />
//                 <select
//                   value={tempUnit}
//                   onChange={(e) => setTempUnit(e.target.value)}
//                 >
//                   <option value="C">°C</option>
//                   <option value="F">°F</option>
//                 </select>
//               </div>
//             </ParameterRow>
//           </div>

//           <div
//             className={`param-section ${ambientTempError ? "error-glow" : ""}`}
//           >
//             <h3>Outdoor Condition</h3>

//             <ParameterRow label="Ambient Temperature">
//               <div className="single-temp-input">
//                 <input
//                   type="number"
//                   className="temp-input"
//                   value={displayTemp(ambientTempC)}
//                   onChange={handleTempChange(setAmbientTempC)}
//                 />
//                 <select
//                   value={tempUnit}
//                   onChange={(e) => setTempUnit(e.target.value)}
//                 >
//                   <option value="C">°C</option>
//                   <option value="F">°F</option>
//                 </select>
//               </div>
//             </ParameterRow>
//           </div>

//           <div className="param-section">
//             <h3>Other Parameters</h3>

//             <ParameterRow label="Type">
//               <div className="temp-input-group">
//                 <select className="parameter-select">
//                   <option>Cooling Capacity</option>
//                 </select>
//               </div>
//             </ParameterRow>

//             <ParameterRow label="Capacity">
//               <div className="dual-temp-inputs">
//                 <div className="temp-input-group">
//                   <input
//                     type="number"
//                     className="temp-input"
//                     defaultValue="400.0"
//                     placeholder="Enter capacity"
//                     aria-label="Cooling capacity"
//                   />
//                   <span className="temp-unit">kW</span>
//                 </div>
//                 {/* <div className="temp-input-group">
//                   <input
//                     type="number"
//                     className="temp-input"
//                     defaultValue="20"
//                     placeholder="Enter tolerance"
//                     aria-label="Capacity tolerance percent"
//                   />
//                   <span className="temp-unit">± [%]</span>
//                 </div> */}
//               </div>
//             </ParameterRow>
//           </div>
//         </section>
//       </div>

//       <footer className="dashboard__footer">
//         <button className="primary" onClick={handleSelectAll}>
//           Select All Models
//         </button>
//         <button className="outline">Eurovent Selection</button>
//       </footer>
//     </div>
//   );
// }

// ###################### CODE VERSION 4 ######################
// with temp toggle button and basic search functionality added with dialog box

// import { useContext, useEffect, useState } from "react";
// import AppContext from "../../context/AppContext";
// import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
// import SearchIcon from "@mui/icons-material/Search";
// import "./Dashboard.css";
// import { useNavigate } from "react-router-dom";
// import { toast } from "react-toastify";
// import {
//   Button,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
// } from "@mui/material";
// import TempToggle from "../../components/TempToggle/TempToggle";

// // Temperature conversion functions
// const celsiusToFahrenheit = (celsius) => {
//   if (!celsius && celsius !== 0) return "";
//   return ((parseFloat(celsius) * 9) / 5 + 32).toFixed(2);
// };

// const fahrenheitToCelsius = (fahrenheit) => {
//   if (!fahrenheit && fahrenheit !== 0) return "";
//   return (((parseFloat(fahrenheit) - 32) * 5) / 9).toFixed(2);
// };

// const ParameterRow = ({ label, children }) => (
//   <div className="parameter-row">
//     <span>{label}</span>
//     <div>{children}</div>
//   </div>
// );

// export default function Dashboard() {
//   const [seriesError, setSeriesError] = useState(false);
//   const [inletTempError, setInletTempError] = useState(false);
//   const [outletTempError, setOutletTempError] = useState(false);
//   const [ambientTempError, setAmbientTempError] = useState(false);
//   const [tempUnit, setTempUnit] = useState("C");
//   const [searchQuery, setSearchQuery] = useState("");

//   // Separate states for inlet temperature in C and F
//   const [inletTempC, setInletTempC] = useState("");
//   const [inletTempF, setInletTempF] = useState("");

//   // Separate states for outlet temperature in C and F
//   const [outletTempC, setOutletTempC] = useState("");
//   const [outletTempF, setOutletTempF] = useState("");

//   // Separate states for ambient temperature in C and F
//   const [ambientTempC, setAmbientTempC] = useState("");
//   const [ambientTempF, setAmbientTempF] = useState("");

//   const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
//   const [searchResults, setSearchResults] = useState([]);
//   const [searchLoading, setSearchLoading] = useState(false);
//   const [selectedSearchRow, setSelectedSearchRow] = useState(null);

//   const {
//     base_URL,
//     seriesList,
//     setSeriesList,
//     selectedSeries,
//     setSelectedSeries,
//     setInletTemp,
//     setOutletTemp,
//     setAmbientTemp,
//   } = useContext(AppContext);

//   const navigate = useNavigate();

//   useEffect(() => {
//     const fetchSeries = async () => {
//       try {
//         const res = await fetch(`${base_URL}/api/series`);
//         const data = await res.json();
//         const codes = data.map((item) => item.series_code);
//         setSeriesList(codes);
//       } catch (error) {
//         console.error("Failed to fetch series:", error);
//       }
//     };

//     fetchSeries();
//   }, [base_URL, setSeriesList]);

//   const metaFilters = [
//     { label: "Subclasses", value: "Air Cooled Chillers" },
//     { label: "Installation Type", value: "Outdoor" },
//     { label: "Application", value: "Domestic cooling" },
//     { label: "Power Supply", value: "All" },
//     { label: "Refrigerant", value: "All" },
//     { label: "Fluid", value: "Water" },
//     { label: "Glycol %", value: "0" },
//   ];

//   const handleSeriesToggle = (seriesCode) => {
//     setSeriesError(false);
//     setSelectedSeries((prev) => {
//       if (prev.includes(seriesCode)) {
//         return prev.filter((code) => code !== seriesCode);
//       }
//       return [...prev, seriesCode];
//     });
//   };

//   const fetchChillersBySeries = async (seriesCode) => {
//     try {
//       setSearchLoading(true);

//       const res = await fetch(
//         `${base_URL}/api/chillers/by-series/${seriesCode}`
//       );

//       const body = await res.json();

//       if (!res.ok) {
//         throw new Error(body?.error || "Failed to fetch chillers");
//       }

//       setSearchResults(body.data || []);
//     } catch (error) {
//       console.error("Search error:", error);
//       toast.error("No chillers found for this series");
//       setSearchResults([]);
//     } finally {
//       setSearchLoading(false);
//     }
//   };

//   const handleSearchProducts = () => {
//     if (!searchQuery.trim()) {
//       toast.warning("Please enter a search term");
//       return;
//     }

//     setIsSearchModalOpen(true);
//     fetchChillersBySeries(searchQuery.trim().toUpperCase());
//   };

//   const handleCloseSearchModal = () => {
//     setIsSearchModalOpen(false);
//     setSearchQuery(""); // ✅ clear input
//     setSearchResults([]);
//   };

//   const applyTemperaturesFromSearch = (row) => {
//     const inletC = row.inlet_temp;
//     const outletC = row.outlet_temp;
//     const ambientC = row.ambient_temp;

//     if (tempUnit === "C") {
//       setInletTempC(inletC);
//       setOutletTempC(outletC);
//       setAmbientTempC(ambientC);

//       setInletTempF(celsiusToFahrenheit(inletC));
//       setOutletTempF(celsiusToFahrenheit(outletC));
//       setAmbientTempF(celsiusToFahrenheit(ambientC));

//       setInletTemp(inletC);
//       setOutletTemp(outletC);
//       setAmbientTemp(ambientC);
//     } else {
//       const inletF = celsiusToFahrenheit(inletC);
//       const outletF = celsiusToFahrenheit(outletC);
//       const ambientF = celsiusToFahrenheit(ambientC);

//       setInletTempF(inletF);
//       setOutletTempF(outletF);
//       setAmbientTempF(ambientF);

//       setInletTempC(inletC);
//       setOutletTempC(outletC);
//       setAmbientTempC(ambientC);

//       setInletTemp(inletC);
//       setOutletTemp(outletC);
//       setAmbientTemp(ambientC);
//     }
//   };

//   const handleSubmitSearchSelection = () => {
//     if (!selectedSearchRow) {
//       toast.warning("Please select a chiller to continue");
//       return;
//     }

//     // 1. Auto-select series
//     setSelectedSeries([searchQuery.toUpperCase()]);

//     // 2. Apply temperatures
//     applyTemperaturesFromSearch(selectedSearchRow);

//     // 3. Close dialog
//     setIsSearchModalOpen(false);
//     setSearchQuery("");
//     setSelectedSearchRow(null);

//     // 4. Navigate
//     // navigate("/result-screen");
//   };

//   const handleSelectAll = () => {
//     setInletTempError(false);
//     setOutletTempError(false);
//     setAmbientTempError(false);

//     if (!selectedSeries.length) {
//       toast.warning("Please select at least one series to continue.");
//       setSeriesError(true);
//       return;
//     }

//     const activeInletTemp = tempUnit === "C" ? inletTempC : inletTempF;
//     const activeOutletTemp = tempUnit === "C" ? outletTempC : outletTempF;

//     let hasErrors = false;

//     if (!activeInletTemp) {
//       setInletTempError(true);
//       hasErrors = true;
//     }
//     if (!activeOutletTemp) {
//       setOutletTempError(true);
//       hasErrors = true;
//     }
//     if (!ambientTempC && !ambientTempF) {
//       setAmbientTempError(true);
//       hasErrors = true;
//     }

//     if (hasErrors) {
//       toast.warning("Please enter all the Temperatures.");
//       return;
//     }
//     navigate("/result-screen");
//   };

//   // Handlers for Inlet Temperature
//   const handleInletTempCChange = (e) => {
//     setInletTempError(false);
//     const value = e.target.value;
//     setInletTempC(value);
//     setInletTempF(celsiusToFahrenheit(value));
//     setInletTemp(value);
//   };

//   const handleInletTempFChange = (e) => {
//     setInletTempError(false);
//     const value = e.target.value;
//     setInletTempF(value);
//     setInletTempC(fahrenheitToCelsius(value));
//     setInletTemp(fahrenheitToCelsius(value));
//   };

//   // Handlers for Outlet Temperature
//   const handleOutletTempCChange = (e) => {
//     setOutletTempError(false);
//     const value = e.target.value;
//     setOutletTempC(value);
//     setOutletTempF(celsiusToFahrenheit(value));
//     setOutletTemp(value);
//   };

//   const handleOutletTempFChange = (e) => {
//     setOutletTempError(false);
//     const value = e.target.value;
//     setOutletTempF(value);
//     setOutletTempC(fahrenheitToCelsius(value));
//     setOutletTemp(fahrenheitToCelsius(value));
//   };

//   // Handlers for Ambient Temperature
//   const handleAmbientTempCChange = (e) => {
//     setAmbientTempError(false);
//     const value = e.target.value;
//     setAmbientTempC(value);
//     setAmbientTempF(celsiusToFahrenheit(value));
//     setAmbientTemp(value);
//   };

//   const handleAmbientTempFChange = (e) => {
//     setAmbientTempError(false);
//     const value = e.target.value;
//     setAmbientTempF(value);
//     setAmbientTempC(fahrenheitToCelsius(value));
//     setAmbientTemp(fahrenheitToCelsius(value));
//   };

//   return (
//     <div className="dashboard">
//       <header className="dashboard__filters">
//         <div className="dashboard__filters-left">
//           <Button
//             variant="outlined"
//             disableElevation
//             onClick={() => navigate(-1)}
//             sx={{
//               minWidth: 44,
//               height: 40,
//               border: "none",
//               borderRadius: "16px",
//               color: "#0f172a",
//               fontWeight: 700,
//             }}
//           >
//             <ArrowBackIosNewIcon sx={{ fontSize: 20, color: "#0f172a" }} />
//           </Button>
//           <div>
//             <p>User</p>
//             <h1>Configurator</h1>
//           </div>
//         </div>

//         <div className="dashboard__search-section">
//           <div className="search-input-wrapper">
//             <SearchIcon sx={{ fontSize: 20, color: "#64748b" }} />
//             <input
//               type="text"
//               className="search-input"
//               placeholder="Search Chiller..."
//               value={searchQuery}
//               onChange={(e) => setSearchQuery(e.target.value)}
//               onKeyPress={(e) => e.key === "Enter" && handleSearchProducts()}
//             />
//           </div>
//           <button className="search-button" onClick={handleSearchProducts}>
//             Search Products
//           </button>
//         </div>
//       </header>

//       {/* Dialog for searching chiller */}
//       <Dialog
//         open={isSearchModalOpen}
//         onClose={handleCloseSearchModal}
//         fullWidth
//         maxWidth="md"
//         PaperProps={{
//           sx: {
//             borderRadius: "20px",
//             // padding: "10px",
//           },
//         }}
//       >
//         {/* ---------- HEADER ---------- */}
//         <DialogTitle
//           sx={{
//             fontWeight: 700,
//             fontSize: 20,
//             color: "#0f172a",
//             pb: 1,
//           }}
//         >
//           Search Results
//           <p
//             style={{
//               marginTop: 6,
//               fontSize: 14,
//               fontWeight: 400,
//               color: "#64748b",
//             }}
//           >
//             Showing results related to "<strong>{searchQuery}</strong>". Select
//             a model to continue.
//           </p>
//         </DialogTitle>

//         {/* ---------- CONTENT ---------- */}
//         <DialogContent dividers sx={{ minHeight: 260, padding: "16px 20px"}} >
//           {/* ---------------- LOADING STATE ---------------- */}
//           {searchLoading && (
//             <p
//               style={{
//                 fontSize: 14,
//                 color: "#64748b",
//                 textAlign: "center",
//                 marginTop: 40,
//               }}
//             >
//               Loading chillers…
//             </p>
//           )}

//           {/* ---------------- EMPTY STATE ---------------- */}
//           {!searchLoading && searchResults.length === 0 && (
//             <p
//               style={{
//                 fontSize: 14,
//                 color: "#94a3b8",
//                 textAlign: "center",
//                 marginTop: 40,
//               }}
//             >
//               No chillers found for this series.
//             </p>
//           )}

//           {/* ---------------- RESULTS TABLE ---------------- */}
//           {!searchLoading && searchResults.length > 0 && (
//             <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
//               {/* Header Row */}
//               <div
//                 style={{
//                   display: "grid",
//                   gridTemplateColumns: "2fr 1fr 1fr 1fr",
//                   fontSize: 13,
//                   fontWeight: 600,
//                   color: "#475569",
//                   padding: "8px 12px",
//                   borderBottom: "1px solid #e2e8f0",
//                 }}
//               >
//                 <span>Model</span>
//                 {/* <span style={{ textAlign: "center" }}>Size</span> */}
//                 <span style={{ textAlign: "center" }}>Inlet (°C)</span>
//                 <span style={{ textAlign: "center" }}>Outlet (°C)</span>
//                 <span style={{ textAlign: "center" }}>Ambient (°C)</span>
//               </div>

//               {/* Data Rows */}
//               {searchResults.map((row) => {
//                 const isSelected =
//                   selectedSearchRow?.chiller_id === row.chiller_id;

//                 return (
//                   <div
//                     key={row.chiller_id}
//                     onClick={() => setSelectedSearchRow(row)}
//                     style={{
//                       display: "grid",
//                       gridTemplateColumns: "2fr 1fr 1fr 1fr",
//                       padding: "10px 12px",
//                       borderRadius: 10,
//                       cursor: "pointer",
//                       backgroundColor: isSelected ? "#eef2ff" : "#ffffff",
//                       border: isSelected
//                         ? "1px solid #6366f1"
//                         : "1px solid #e2e8f0",
//                       fontSize: 14,
//                       alignItems: "center",
//                     }}
//                   >
//                     <div>
//                       <strong style={{ color: "#0f172a" }}>{row.model}</strong>
//                     </div>

//                     {/* <span style={{ textAlign: "center" }}>{row.model_size}</span> */}
//                     <span style={{ textAlign: "center" }}>
//                       {row.inlet_temp}
//                     </span>
//                     <span style={{ textAlign: "center" }}>
//                       {row.outlet_temp}
//                     </span>
//                     <span style={{ textAlign: "center" }}>
//                       {row.ambient_temp}
//                     </span>
//                   </div>
//                 );
//               })}
//             </div>
//           )}
//         </DialogContent>

//         {/* ---------- FOOTER ---------- */}
//         <DialogActions
//           sx={{
//             padding: "16px 20px",
//             display: "flex",
//             justifyContent: "flex-end",
//             gap: "2px",
//           }}
//         >
//           <Button
//             variant="outlined"
//             className="dialog-secondary-button"
//             onClick={handleCloseSearchModal}
//             sx={{
//               borderRadius: "12px",
//               textTransform: "none",
//               fontWeight: 600,
//             }}
//           >
//             Close
//           </Button>

//           <Button
//             variant="contained"
//             className="dialog-primary-button"
//             sx={{
//               borderRadius: "12px",
//               textTransform: "none",
//               fontWeight: 600,
//             }}
//             disabled={!selectedSearchRow}
//             onClick={handleSubmitSearchSelection}
//           >
//             Submit
//           </Button>
//         </DialogActions>
//       </Dialog>

//       <div className="dashboard__meta-filters">
//         <div className="filter-chip-group">
//           {metaFilters.map((item) => (
//             <div className="chip-field" key={item.label}>
//               <label>{item.label}</label>
//               {item.label.includes("%") || item.label.includes("Factor") ? (
//                 <input type="number" placeholder={item.value} />
//               ) : (
//                 <select>
//                   <option>{item.value}</option>
//                 </select>
//               )}
//             </div>
//           ))}
//         </div>
//       </div>

//       <div className="dashboard__grid">
//         <section className={`panel panel--series`}>
//           <div className="panel__header">
//             <h2>Series</h2>
//             <p>Choose one or multiple items from the list</p>
//           </div>

//           <div className={`series-table ${seriesError ? "error-glow" : ""}`}>
//             <div className="series-table__head">
//               <span>Series</span>
//               <span>Version</span>
//               <span>Option</span>
//             </div>

//             <div className="series-table__body">
//               {seriesList.map((model) => (
//                 <label className="series-row" key={model}>
//                   <div className="series-row__main">
//                     <input
//                       type="checkbox"
//                       checked={selectedSeries.includes(model)}
//                       onChange={() => handleSeriesToggle(model)}
//                     />
//                     <span>{model}</span>
//                   </div>
//                   <span className="muted">—</span>
//                   <span className="muted">—</span>
//                 </label>
//               ))}
//             </div>
//           </div>
//         </section>

//         <section className="panel panel--parameters">
//           <div
//             style={{
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "end",
//             }}
//           >
//             <TempToggle value={tempUnit} onChange={setTempUnit} />
//           </div>

//           <div
//             className={`param-section ${
//               inletTempError || outletTempError ? "error-glow" : ""
//             }`}
//           >
//             <h3>Chilled Water Temperature</h3>

//             <ParameterRow label="Chiller Inlet Temperature">
//               <div className="dual-temp-inputs">
//                 <div className="temp-input-group">
//                   <input
//                     type="number"
//                     className="temp-input"
//                     value={inletTempC}
//                     onChange={handleInletTempCChange}
//                     disabled={tempUnit === "F"}
//                     aria-label="Chiller inlet temperature Celsius"
//                   />
//                   <span className="temp-unit">°C</span>
//                 </div>
//                 <div className="temp-input-group">
//                   <input
//                     type="number"
//                     className="temp-input"
//                     value={inletTempF}
//                     onChange={handleInletTempFChange}
//                     disabled={tempUnit === "C"}
//                     aria-label="Chiller inlet temperature Fahrenheit"
//                   />
//                   <span className="temp-unit">°F</span>
//                 </div>
//               </div>
//             </ParameterRow>

//             <ParameterRow label="Chiller Outlet Temperature">
//               <div className="dual-temp-inputs">
//                 <div className="temp-input-group">
//                   <input
//                     type="number"
//                     className="temp-input"
//                     value={outletTempC}
//                     onChange={handleOutletTempCChange}
//                     disabled={tempUnit === "F"}
//                     aria-label="Chiller outlet temperature Celsius"
//                   />
//                   <span className="temp-unit">°C</span>
//                 </div>
//                 <div className="temp-input-group">
//                   <input
//                     type="number"
//                     className="temp-input"
//                     value={outletTempF}
//                     onChange={handleOutletTempFChange}
//                     disabled={tempUnit === "C"}
//                     aria-label="Chiller outlet temperature Fahrenheit"
//                   />
//                   <span className="temp-unit">°F</span>
//                 </div>
//               </div>
//             </ParameterRow>
//           </div>

//           <div
//             className={`param-section ${ambientTempError ? "error-glow" : ""}`}
//           >
//             <h3>Outdoor Condition</h3>

//             <ParameterRow label="Ambient Temperature">
//               <div className="dual-temp-inputs">
//                 <div className="temp-input-group">
//                   <input
//                     type="number"
//                     className="temp-input"
//                     value={ambientTempC}
//                     onChange={handleAmbientTempCChange}
//                     disabled={tempUnit === "F"}
//                     aria-label="Chiller ambient temperature Celsius"
//                   />
//                   <span className="temp-unit">°C</span>
//                 </div>
//                 <div className="temp-input-group">
//                   <input
//                     type="number"
//                     className="temp-input"
//                     value={ambientTempF}
//                     onChange={handleAmbientTempFChange}
//                     disabled={tempUnit === "C"}
//                     aria-label="Chiller ambient temperature Fahrenheit"
//                   />
//                   <span className="temp-unit">°F</span>
//                 </div>
//               </div>
//             </ParameterRow>
//           </div>

//           <div className="param-section">
//             <h3>Other Parameters</h3>

//             <ParameterRow label="Type">
//               <div className="temp-input-group">
//                 <select className="parameter-select">
//                   <option>Cooling Capacity</option>
//                 </select>
//               </div>
//             </ParameterRow>

//             <ParameterRow label="Capacity">
//               <div className="dual-temp-inputs">
//                 <div className="temp-input-group">
//                   <input
//                     type="number"
//                     className="temp-input"
//                     defaultValue="400.0"
//                     placeholder="Enter capacity"
//                     aria-label="Cooling capacity"
//                   />
//                   <span className="temp-unit">kW</span>
//                 </div>
//                 <div className="temp-input-group">
//                   <input
//                     type="number"
//                     className="temp-input"
//                     defaultValue="20"
//                     placeholder="Enter tolerance"
//                     aria-label="Capacity tolerance percent"
//                   />
//                   <span className="temp-unit">± [%]</span>
//                 </div>
//               </div>
//             </ParameterRow>
//           </div>
//         </section>
//       </div>

//       <footer className="dashboard__footer">
//         <button className="primary" onClick={handleSelectAll}>
//           Select All Models
//         </button>
//         <button className="outline">Eurovent Selection</button>
//       </footer>
//     </div>
//   );
// }

// ###################### CODE VERSION 3 ######################

// import { useContext, useEffect, useState } from "react";
// import AppContext from "../../context/AppContext";
// import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
// import "./Dashboard.css";
// import { useNavigate } from "react-router-dom";
// import { toast } from "react-toastify";
// import { Button } from "@mui/material";
// import TempToggle from "../../components/TempToggle/TempToggle";

// // Temperature conversion functions
// const celsiusToFahrenheit = (celsius) => {
//   if (!celsius && celsius !== 0) return "";
//   return ((parseFloat(celsius) * 9) / 5 + 32).toFixed(2);
// };

// const fahrenheitToCelsius = (fahrenheit) => {
//   if (!fahrenheit && fahrenheit !== 0) return "";
//   return (((parseFloat(fahrenheit) - 32) * 5) / 9).toFixed(2);
// };

// // const FilterBlock = ({ label, value }) => (
// //   <div className="filter-tile">
// //     <p>{label}</p>
// //     <select defaultValue={value}>
// //       <option>{value}</option>
// //     </select>
// //   </div>
// // );

// const ParameterRow = ({ label, children }) => (
//   <div className="parameter-row">
//     <span>{label}</span>
//     <div>{children}</div>
//   </div>
// );

// export default function Dashboard() {

//   const [seriesError, setSeriesError] = useState(false);
//   const [inletTempError, setInletTempError] = useState(false);
//   const [outletTempError, setOutletTempError] = useState(false);
//   const [ambientTempError, setAmbientTempError] = useState(false);
//   const [tempUnit, setTempUnit] = useState("C"); // default °C

//   // Separate states for inlet temperature in C and F
//   const [inletTempC, setInletTempC] = useState("");
//   const [inletTempF, setInletTempF] = useState("");

//   // Separate states for outlet temperature in C and F
//   const [outletTempC, setOutletTempC] = useState("");
//   const [outletTempF, setOutletTempF] = useState("");

//   // Separate states for ambient temperature in C and F
//   const [ambientTempC, setAmbientTempC] = useState("");
//   const [ambientTempF, setAmbientTempF] = useState("");

//   const {
//     base_URL,
//     seriesList,
//     setSeriesList,
//     selectedSeries,
//     setSelectedSeries,
//     // inletTemp,
//     setInletTemp,
//     // outletTemp,
//     setOutletTemp,
//     // ambientTemp,
//     setAmbientTemp,
//   } = useContext(AppContext);

//   const navigate = useNavigate();

//   // ✅ NEW: fetch data from backend
//   useEffect(() => {
//     const fetchSeries = async () => {
//       try {
//         const res = await fetch(`${base_URL}/api/series`);
//         const data = await res.json();

//         // extract only series_code
//         const codes = data.map((item) => item.series_code);

//         setSeriesList(codes);
//       } catch (error) {
//         console.error("Failed to fetch series:", error);
//       }
//     };

//     fetchSeries();
//   }, [base_URL, setSeriesList]);

//   const metaFilters = [
//     { label: "Subclasses", value: "Air Cooled Chillers" },
//     { label: "Installation Type", value: "Outdoor" },
//     { label: "Application", value: "Domestic cooling" },
//     { label: "Power Supply", value: "All" },
//     { label: "Refrigerant", value: "All" },
//     { label: "Fluid", value: "Water" },
//     { label: "Glycol %", value: "0" },
//     // { label: "Faulting Factor", value: "0.000" },
//   ];

//   // const leftFilters = [
//   //   { label: "Subclasses", value: "Air Cooled Chillers" },
//   //   { label: "Installation Type", value: "Outdoor" },
//   //   { label: "Application", value: "Domestic cooling" },
//   //   { label: "Power Supply", value: "All" },
//   //   { label: "Refrigerant", value: "All" },
//   // ];

//   const handleSeriesToggle = (seriesCode) => {
//     setSeriesError(false); // Remove red glow once user interacts

//     setSelectedSeries((prev) => {
//       if (prev.includes(seriesCode)) {
//         return prev.filter((code) => code !== seriesCode);
//       }
//       return [...prev, seriesCode];
//     });
//   };

//   const handleSelectAll = () => {
//     // Clear all errors first
//     setInletTempError(false);
//     setOutletTempError(false);
//     setAmbientTempError(false);

//     if (!selectedSeries.length) {
//       toast.warning("Please select at least one series to continue.");
//       setSeriesError(true); // Show red glow
//       return;
//     }

//     // Use the active temperature values based on tempUnit
//     const activeInletTemp = tempUnit === "C" ? inletTempC : inletTempF;
//     const activeOutletTemp = tempUnit === "C" ? outletTempC : outletTempF;

//     let hasErrors = false;

//     if (!activeInletTemp) {
//       setInletTempError(true);
//       hasErrors = true;
//     }
//     if (!activeOutletTemp) {
//       setOutletTempError(true);
//       hasErrors = true;
//     }
//     if (!ambientTempC && !ambientTempF) {
//       setAmbientTempError(true);
//       hasErrors = true;
//     }

//     if (hasErrors) {
//       toast.warning("Please enter all the Temperatures.");
//       return;
//     }
//     navigate("/result-screen");
//   };

//   // Handlers for Inlet Temperature
//   const handleInletTempCChange = (e) => {
//     setInletTempError(false);
//     const value = e.target.value;
//     setInletTempC(value);
//     setInletTempF(celsiusToFahrenheit(value));
//     setInletTemp(value); // Update context
//   };

//   const handleInletTempFChange = (e) => {
//     setInletTempError(false);
//     const value = e.target.value;
//     setInletTempF(value);
//     setInletTempC(fahrenheitToCelsius(value));
//     setInletTemp(fahrenheitToCelsius(value)); // Update context with C value
//   };

//   // Handlers for Outlet Temperature
//   const handleOutletTempCChange = (e) => {
//     setOutletTempError(false);
//     const value = e.target.value;
//     setOutletTempC(value);
//     setOutletTempF(celsiusToFahrenheit(value));
//     setOutletTemp(value); // Update context
//   };

//   const handleOutletTempFChange = (e) => {
//     setOutletTempError(false);
//     const value = e.target.value;
//     setOutletTempF(value);
//     setOutletTempC(fahrenheitToCelsius(value));
//     setOutletTemp(fahrenheitToCelsius(value)); // Update context with C value
//   };

//   // Handlers for Ambient Temperature
//   const handleAmbientTempCChange = (e) => {
//     setAmbientTempError(false);
//     const value = e.target.value;
//     setAmbientTempC(value);
//     setAmbientTempF(celsiusToFahrenheit(value));
//     setAmbientTemp(value); // Update context
//   };

//   const handleAmbientTempFChange = (e) => {
//     setAmbientTempError(false);
//     const value = e.target.value;
//     setAmbientTempF(value);
//     setAmbientTempC(fahrenheitToCelsius(value));
//     setAmbientTemp(fahrenheitToCelsius(value)); // Update context with C value
//   };

//   return (
//     <div className="dashboard">
//       <header className="dashboard__filters">
//         <div className="dashboard__filters-left">
//           <Button
//             variant="outlined"
//             disableElevation
//             onClick={() => navigate(-1)}
//             sx={{
//               minWidth: 44,
//               height: 40,
//               border: "none",
//               borderRadius: "16px",
//               color: "#0f172a",
//               fontWeight: 700,
//             }}
//           >
//             <ArrowBackIosNewIcon sx={{ fontSize: 20, color: "#0f172a" }} />
//           </Button>
//           <div>
//             <p>User</p>
//             <h1>Configurator</h1>
//           </div>
//         </div>
//         <div className="filter-chip-group">
//           {metaFilters.map((item) => (
//             <div className="chip-field" key={item.label}>
//               <label>{item.label}</label>
//               {item.label.includes("%") || item.label.includes("Factor") ? (
//                 <input type="number" placeholder={item.value} />
//               ) : (
//                 <select>
//                   <option>{item.value}</option>
//                 </select>
//               )}
//             </div>
//           ))}
//         </div>
//       </header>

//       <div className="dashboard__grid">
//         {/* <aside className="panel panel--filters">
//           <div className="panel__header">
//             <h2>Filters</h2>
//             <p>Define the search context</p>
//           </div>

//           <div className="panel__content">
//             {leftFilters.map((filter) => (
//               <FilterBlock
//                 key={filter.label}
//                 label={filter.label}
//                 value={filter.value}
//               />
//             ))}
//           </div>
//         </aside> */}

//         <section className={`panel panel--series`}>
//           <div className="panel__header">
//             <h2>Series</h2>
//             <p>Choose one or multiple items from the list</p>
//           </div>

//           <div className={`series-table ${seriesError ? "error-glow" : ""}`}>
//             <div className="series-table__head">
//               <span>Series</span>
//               <span>Version</span>
//               <span>Option</span>
//             </div>

//             <div className="series-table__body">
//               {seriesList.map((model) => (
//                 <label className="series-row" key={model}>
//                   <div className="series-row__main">
//                     <input
//                       type="checkbox"
//                       checked={selectedSeries.includes(model)}
//                       onChange={() => handleSeriesToggle(model)}
//                     />
//                     <span>{model}</span>
//                   </div>
//                   <span className="muted">—</span>
//                   <span className="muted">—</span>
//                 </label>
//               ))}
//             </div>
//           </div>
//         </section>

//         <section className="panel panel--parameters">
//           <div style={{ display: "flex", alignItems: "center", justifyContent: "end" }}>
//             <TempToggle value={tempUnit} onChange={setTempUnit} />  {/* Temperature Toggle */}
//           </div>

//           <div className={`param-section ${inletTempError || outletTempError ? "error-glow" : ""}`}>
//             <h3>Chilled Water Temperature</h3>

//             <ParameterRow label="Chiller Inlet Temperature">
//               <div className="dual-temp-inputs">
//                 <div className="temp-input-group">
//                   <input
//                     type="number"
//                     className="temp-input"
//                     value={inletTempC}
//                     onChange={handleInletTempCChange}
//                     disabled={tempUnit === "F"}
//                     aria-label="Chiller inlet temperature Celsius"
//                   />
//                   <span className="temp-unit">°C</span>
//                 </div>
//                 <div className="temp-input-group">
//                   <input
//                     type="number"
//                     className="temp-input"
//                     value={inletTempF}
//                     onChange={handleInletTempFChange}
//                     disabled={tempUnit === "C"}
//                     aria-label="Chiller inlet temperature Fahrenheit"
//                   />
//                   <span className="temp-unit">°F</span>
//                 </div>
//               </div>
//             </ParameterRow>

//             <ParameterRow label="Chiller Outlet Temperature">
//               <div className="dual-temp-inputs">
//                 <div className="temp-input-group">
//                   <input
//                     type="number"
//                     className="temp-input"
//                     value={outletTempC}
//                     onChange={handleOutletTempCChange}
//                     disabled={tempUnit === "F"}
//                     aria-label="Chiller outlet temperature Celsius"
//                   />
//                   <span className="temp-unit">°C</span>
//                 </div>
//                 <div className="temp-input-group">
//                   <input
//                     type="number"
//                     className="temp-input"
//                     value={outletTempF}
//                     onChange={handleOutletTempFChange}
//                     disabled={tempUnit === "C"}
//                     aria-label="Chiller outlet temperature Fahrenheit"
//                   />
//                   <span className="temp-unit">°F</span>
//                 </div>
//               </div>
//             </ParameterRow>
//           </div>

//           <div className={`param-section ${ambientTempError ? "error-glow" : ""}`}>
//             <h3>Outdoor Condition</h3>

//             <ParameterRow label="Ambient Temperature">
//               <div className="dual-temp-inputs">
//                 <div className="temp-input-group">
//                   <input
//                     type="number"
//                     className="temp-input"
//                     value={ambientTempC}
//                     onChange={handleAmbientTempCChange}
//                     disabled={tempUnit === "F"}
//                     aria-label="Chiller ambient temperature Celsius"
//                   />
//                   <span className="temp-unit">°C</span>
//                 </div>
//                 <div className="temp-input-group">
//                   <input
//                     type="number"
//                     className="temp-input"
//                     value={ambientTempF}
//                     onChange={handleAmbientTempFChange}
//                     disabled={tempUnit === "C"}
//                     aria-label="Chiller ambient temperature Fahrenheit"
//                   />
//                   <span className="temp-unit">°F</span>
//                 </div>
//               </div>
//             </ParameterRow>
//           </div>

//           <div className="param-section">
//             <h3>Other Parameters</h3>

//             <ParameterRow label="Type">
//               <div className="temp-input-group" >
//                 <select className="parameter-select">
//                   <option>Cooling Capacity</option>
//                   {/* <option>Heating Capacity</option> */}
//                 </select>
//               </div>
//             </ParameterRow>

//             <ParameterRow label="Capacity">
//               <div className="dual-temp-inputs">
//                 <div className="temp-input-group">
//                   <input
//                     type="number"
//                     className="temp-input"
//                     defaultValue="400.0"
//                     placeholder="Enter capacity"
//                     aria-label="Cooling capacity"
//                   />
//                   <span className="temp-unit">kW</span>
//                 </div>
//                 <div className="temp-input-group">
//                   <input
//                     type="number"
//                     className="temp-input"
//                     defaultValue="20"
//                     placeholder="Enter tolerance"
//                     aria-label="Capacity tolerance percent"
//                   />
//                   <span className="temp-unit">± [%]</span>
//                 </div>
//               </div>
//             </ParameterRow>

//             {/* <ParameterRow label="Noise [dB(A)]">
//               <input type="number" defaultValue="0" />
//             </ParameterRow>

//             <ParameterRow label="Efficiency">
//               <select>
//                 <option>EEER [kW/kW]</option>
//               </select>
//               <input type="number" defaultValue="0.000" />
//               <input type="number" defaultValue="10" />
//             </ParameterRow>

//             <ParameterRow label="EER [kW/kW]">
//               <input type="number" defaultValue="0.000" />
//               <input type="number" defaultValue="10" />
//             </ParameterRow> */}
//           </div>
//         </section>
//       </div>

//       <footer className="dashboard__footer">
//         <button className="ghost">Search Products</button>
//         <button className="primary" onClick={handleSelectAll}>
//           Select All Models
//         </button>
//         <button className="outline">Eurovent Selection</button>
//       </footer>
//     </div>
//   );
// }

// ###################### CODE VERSION 2 ######################

// import { useContext, useEffect, useState } from "react";
// import AppContext from "../../context/AppContext";
// import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
// import "./Dashboard.css";
// import { useNavigate } from "react-router-dom";
// import { toast } from "react-toastify";
// import { Button } from "@mui/material";

// // const FilterBlock = ({ label, value }) => (
// //   <div className="filter-tile">
// //     <p>{label}</p>
// //     <select defaultValue={value}>
// //       <option>{value}</option>
// //     </select>
// //   </div>
// // );

// const ParameterRow = ({ label, children }) => (
//   <div className="parameter-row">
//     <span>{label}</span>
//     <div>{children}</div>
//   </div>
// );

// export default function Dashboard() {

//   const [validationError, setValidationError] = useState(false);

//   const {
//     base_URL,
//     seriesList,
//     setSeriesList,
//     selectedSeries,
//     setSelectedSeries,
//     inletTemp,
//     setInletTemp,
//     outletTemp,
//     setOutletTemp,
//     ambientTemp,
//     setAmbientTemp,
//   } = useContext(AppContext);

//   const navigate = useNavigate();

//   // ✅ NEW: fetch data from backend
//   useEffect(() => {
//     const fetchSeries = async () => {
//       try {
//         const res = await fetch(`${base_URL}/api/series`);
//         const data = await res.json();

//         // extract only series_code
//         const codes = data.map((item) => item.series_code);

//         setSeriesList(codes);
//       } catch (error) {
//         console.error("Failed to fetch series:", error);
//       }
//     };

//     fetchSeries();
//   }, [base_URL, setSeriesList]);

//   const metaFilters = [
//     { label: "Subclasses", value: "Air Cooled Chillers" },
//     { label: "Installation Type", value: "Outdoor" },
//     { label: "Application", value: "Domestic cooling" },
//     { label: "Power Supply", value: "All" },
//     { label: "Refrigerant", value: "All" },
//     { label: "Fluid", value: "Water" },
//     { label: "Glycol %", value: "0" },
//     // { label: "Faulting Factor", value: "0.000" },
//   ];

//   // const leftFilters = [
//   //   { label: "Subclasses", value: "Air Cooled Chillers" },
//   //   { label: "Installation Type", value: "Outdoor" },
//   //   { label: "Application", value: "Domestic cooling" },
//   //   { label: "Power Supply", value: "All" },
//   //   { label: "Refrigerant", value: "All" },
//   // ];

//   const handleSeriesToggle = (seriesCode) => {
//     setValidationError(false); // Remove red glow once user interacts

//     setSelectedSeries((prev) => {
//       if (prev.includes(seriesCode)) {
//         return prev.filter((code) => code !== seriesCode);
//       }
//       return [...prev, seriesCode];
//     });
//   };

//   const handleSelectAll = () => {
//     if (!selectedSeries.length) {
//       toast.warning("Please select at least one series to continue.");
//       setValidationError(true); // Show red glow
//       return;
//     }
//     if (!inletTemp || !outletTemp || !ambientTemp) {
//       toast.warning("Please enter all the Temperatures.");
//       return;
//     }
//     navigate("/result-screen");
//   };

//   return (
//     <div className="dashboard">
//       <header className="dashboard__filters">
//         <div className="dashboard__filters-left">
//           <Button
//             variant="outlined"
//             disableElevation
//             onClick={() => navigate(-1)}
//             sx={{
//               minWidth: 44,
//               height: 40,
//               border: "none",
//               borderRadius: "16px",
//               color: "#0f172a",
//               fontWeight: 700,
//             }}
//           >
//             <ArrowBackIosNewIcon sx={{ fontSize: 20, color: "#0f172a" }} />
//           </Button>
//           <div>
//             <p>User</p>
//             <h1>Configurator</h1>
//           </div>
//         </div>
//         <div className="filter-chip-group">
//           {metaFilters.map((item) => (
//             <div className="chip-field" key={item.label}>
//               <label>{item.label}</label>
//               {item.label.includes("%") || item.label.includes("Factor") ? (
//                 <input type="number" placeholder={item.value} />
//               ) : (
//                 <select>
//                   <option>{item.value}</option>
//                 </select>
//               )}
//             </div>
//           ))}
//         </div>
//       </header>

//       <div className="dashboard__grid">
//         {/* <aside className="panel panel--filters">
//           <div className="panel__header">
//             <h2>Filters</h2>
//             <p>Define the search context</p>
//           </div>

//           <div className="panel__content">
//             {leftFilters.map((filter) => (
//               <FilterBlock
//                 key={filter.label}
//                 label={filter.label}
//                 value={filter.value}
//               />
//             ))}
//           </div>
//         </aside> */}

//         <section className={`panel panel--series`}>
//           <div className="panel__header">
//             <h2>Series</h2>
//             <p>Choose one or multiple items from the list</p>
//           </div>

//           <div className={`series-table`}>
//             <div className="series-table__head">
//               <span>Series</span>
//               <span>Version</span>
//               <span>Option</span>
//             </div>

//             <div className="series-table__body">
//               {seriesList.map((model) => (
//                 <label className="series-row" key={model}>
//                   <div className="series-row__main">
//                     <input
//                       type="checkbox"
//                       checked={selectedSeries.includes(model)}
//                       onChange={() => handleSeriesToggle(model)}
//                     />
//                     <span>{model}</span>
//                   </div>
//                   <span className="muted">—</span>
//                   <span className="muted">—</span>
//                 </label>
//               ))}
//             </div>
//           </div>
//         </section>

//         <section className="panel panel--parameters">
//           <div className="param-section">
//             <h3>Chilled Water Temperature</h3>

//             <ParameterRow label="Chiller Inlet Temperature">
//               <input
//                 type="number"
//                 value={inletTemp}
//                 onChange={(e) => setInletTemp(e.target.value)}
//               />
//               <select>
//                 <option>°C</option>
//                 <option>°F</option>
//               </select>
//             </ParameterRow>

//             <ParameterRow label="Chiller Outlet Temperature">
//               <input
//                 type="number"
//                 value={outletTemp}
//                 onChange={(e) => setOutletTemp(e.target.value)}
//               />
//               <select>
//                 <option>°C</option>
//                 <option>°F</option>
//               </select>
//             </ParameterRow>
//           </div>

//           <div className="param-section">
//             <h3>Outdoor Condition</h3>

//             <ParameterRow label="Ambient Temperature">
//               <input
//                 type="number"
//                 value={ambientTemp}
//                 onChange={(e) => setAmbientTemp(e.target.value)}
//               />
//               <select>
//                 <option>°C</option>
//                 <option>°F</option>
//               </select>
//             </ParameterRow>
//           </div>

//           <div className="param-section">
//             <h3>Other Parameters</h3>

//             <ParameterRow label="Type">
//               <select>
//                 <option>Cooling Capacity</option>
//               </select>
//             </ParameterRow>

//             <ParameterRow label="Capacity [kW]">
//               <input type="number" defaultValue="400.0" />
//               <div className="parameter-inline">
//                 <label htmlFor="capacity-tolerance">± [%]</label>
//                 <input
//                   id="capacity-tolerance"
//                   aria-label="Capacity tolerance percent"
//                   type="number"
//                   defaultValue="20"
//                 />
//               </div>
//             </ParameterRow>

//             {/* <ParameterRow label="Noise [dB(A)]">
//               <input type="number" defaultValue="0" />
//             </ParameterRow>

//             <ParameterRow label="Efficiency">
//               <select>
//                 <option>EEER [kW/kW]</option>
//               </select>
//               <input type="number" defaultValue="0.000" />
//               <input type="number" defaultValue="10" />
//             </ParameterRow>

//             <ParameterRow label="EER [kW/kW]">
//               <input type="number" defaultValue="0.000" />
//               <input type="number" defaultValue="10" />
//             </ParameterRow> */}
//           </div>
//         </section>
//       </div>

//       <footer className="dashboard__footer">
//         <button className="ghost">Search Products</button>
//         <button className="primary" onClick={handleSelectAll}>
//           Select All Models
//         </button>
//         <button className="outline">Eurovent Selection</button>
//         <button className="badge">Logo</button>
//       </footer>
//     </div>
//   );
// }

// ###################### CODE VERSION 1 ######################
// import React from "react";
// import {
//   Box,
//   Grid,
//   Paper,
//   Typography,
//   FormControl,
//   Select,
//   MenuItem,
//   TextField,
//   Button,
//   InputLabel,
//   Checkbox,
//   Divider,
// } from "@mui/material";

// export default function SearchScreen() {
//   return (
//     <Box p={1}>
//       <Paper elevation={1} sx={{ p: 1, borderRadius: 2 }}>
//         {/* TOP CONTROLS (Fluid, Water, Glycol %, Faulting Factor) */}
//         <Box
//           display="flex"
//           justifyContent="flex-end"
//           gap="16px"
//           alignItems="center"
//           mb={3}
//         >
//           {/* Fluid */}
//           <Box width="220px">
//             <FormControl fullWidth size="small">
//               <InputLabel id="fluid-label">Fluid</InputLabel>

//               <Select
//                 labelId="fluid-label"
//                 id="fluid-select"
//                 label="Fluid"
//                 defaultValue=""
//               >
//                 <MenuItem value="fluid1">Fluid 1</MenuItem>
//                 <MenuItem value="fluid2">Fluid 2</MenuItem>
//               </Select>
//             </FormControl>
//           </Box>

//           {/* Water */}
//           <Box width="220px">
//             <FormControl fullWidth size="small">
//               <InputLabel id="water-label">Water</InputLabel>

//               <Select
//                 labelId="water-label"
//                 id="water-select"
//                 label="Water"
//                 defaultValue=""
//               >
//                 <MenuItem value="water">Water</MenuItem>
//                 <MenuItem value="brine">Brine</MenuItem>
//               </Select>
//             </FormControl>
//           </Box>

//           {/* Glycol % */}
//           <Box width="220px">
//             <TextField
//               label="Glycol %"
//               fullWidth
//               size="small"
//               defaultValue={0}
//             />
//           </Box>

//           {/* Faulting Factor */}
//           <Box width="220px">
//             <TextField
//               label="Faulting Fac."
//               fullWidth
//               size="small"
//               defaultValue={0.0}
//             />
//           </Box>
//         </Box>

//         <Grid container spacing={2}>
//           {/* LEFT FILTER PANEL */}
//           <Grid item xs={12} md={3}>
//             <Paper
//               sx={{
//                 height: "72vh",
//                 overflowY: "auto",
//                 p: 2,
//                 borderRadius: 2,
//               }}
//             >
//               <FilterDropdown
//                 label="SUBCLASSES"
//                 items={["Air Cooled Chillers"]}
//               />
//               <FilterDropdown label="INSTALLATION TYPE" items={["Outdoor"]} />
//               <FilterDropdown label="HEAT RECOVERY TYPE" items={["Without"]} />
//               <FilterDropdown
//                 label="REVERSAL CYCLE TYPE"
//                 items={["Select type"]}
//               />
//               <FilterDropdown label="APPLICATION" items={["Standard"]} />
//               <FilterDropdown label="POWER SUPPLY" items={["All"]} />
//               <FilterDropdown label="REFRIGERANT" items={["All"]} />
//             </Paper>
//           </Grid>

//           {/* MIDDLE RANGE/VERSIONS/OPTIONS PANEL */}
//           <Grid item xs={12} md={3}>
//             <Paper
//               sx={{
//                 height: "72vh",
//                 overflowY: "auto",
//                 p: 2,
//                 borderRadius: 2,
//               }}
//             >
//               <Typography variant="subtitle2" mb={1}>
//                 RANGE
//               </Typography>

//               {/* Example list (replace with real data) */}
//               {["PK2-061", "PK2-064", "LK02-037", "LFK2-094", "IN2-082"].map(
//                 (item, i) => (
//                   <Box key={i} display="flex" alignItems="center" mb={1}>
//                     <Checkbox size="small" />
//                     <Typography fontSize={14}>{item}</Typography>
//                   </Box>
//                 )
//               )}
//             </Paper>
//           </Grid>

//           {/* RIGHT PANEL – PARAMETERS */}
//           <Grid item xs={12} md={6}>
//             <Paper
//               sx={{
//                 height: "72vh",
//                 overflowY: "auto",
//                 p: 2,
//                 borderRadius: 2,
//               }}
//             >
//               {/* SECTION: Heat Exchanger User Side */}
//               <SectionLabel title="HEAT EXCHANGER USER SIDE" />

//               <TwoFieldRow
//                 label="Inlet temperature"
//                 unit="°C"
//                 defaultValue="20.00"
//               />
//               <TwoFieldRow
//                 label="Outlet temperature"
//                 unit="°C"
//                 defaultValue="15.00"
//               />

//               <SectionLabel title="OUTDOOR CONDITION" />
//               <TextField
//                 fullWidth
//                 size="small"
//                 label="Temperature °C"
//                 defaultValue="10"
//                 sx={{ mb: 3 }}
//               />

//               <SectionLabel title="OTHER PARAMETERS" />

//               <Grid container spacing={2}>
//                 <Grid item xs={12} sm={6}>
//                   <FormControl fullWidth size="small">
//                     <InputLabel>Type</InputLabel>
//                     <Select defaultValue="">
//                       <MenuItem value="Cooling">Cooling Capacity</MenuItem>
//                       <MenuItem value="Heating">Heating Capacity</MenuItem>
//                     </Select>
//                   </FormControl>
//                 </Grid>

//                 <Grid item xs={12} sm={6}>
//                   <TextField
//                     fullWidth
//                     size="small"
//                     label="Capacity [kW]"
//                     defaultValue="400.0"
//                   />
//                 </Grid>

//                 <Grid item xs={12} sm={6}>
//                   <TextField
//                     fullWidth
//                     size="small"
//                     label="Noise [dB(A)]"
//                     defaultValue="0"
//                   />
//                 </Grid>

//                 <Grid item xs={12} sm={6}>
//                   <TextField
//                     fullWidth
//                     size="small"
//                     label="Efficiency (EER [kW/kW])"
//                     defaultValue="10"
//                   />
//                 </Grid>
//               </Grid>
//             </Paper>
//           </Grid>
//         </Grid>

//         {/* FOOTER BUTTONS */}
//         <Grid container spacing={2} mt={3} alignItems="center">
//           <Grid item xs={12} md={9}>
//             <Box display="flex" gap={2}>
//               <Button
//                 variant="contained"
//                 sx={{ background: "#4B5563", textTransform: "none" }}
//               >
//                 Search Products
//               </Button>

//               <Button
//                 variant="contained"
//                 sx={{ background: "#1D4ED8", textTransform: "none" }}
//               >
//                 Select All Models
//               </Button>

//               <Button
//                 variant="contained"
//                 sx={{ background: "#059669", textTransform: "none" }}
//               >
//                 Eurovent Selection
//               </Button>
//             </Box>
//           </Grid>

//           <Grid item xs={12} md={3}>
//             <Box
//               sx={{
//                 background: "#FBBF24",
//                 padding: "12px",
//                 borderRadius: 1,
//                 textAlign: "center",
//                 fontWeight: 700,
//                 fontSize: 18,
//               }}
//             >
//               Logo
//             </Box>
//           </Grid>
//         </Grid>
//       </Paper>
//     </Box>
//   );
// }

// /* --------------------------------------------
//    REUSABLE COMPONENTS
// --------------------------------------------- */

// function FilterDropdown({ label, items }) {
//   return (
//     <Box mb={2}>
//       <Typography fontSize={13} fontWeight={600} mb={1}>
//         {label}
//       </Typography>
//       <FormControl fullWidth size="small">
//         <Select defaultValue={items[0]}>
//           {items.map((opt, idx) => (
//             <MenuItem key={idx} value={opt}>
//               {opt}
//             </MenuItem>
//           ))}
//         </Select>
//       </FormControl>
//     </Box>
//   );
// }

// function SectionLabel({ title }) {
//   return (
//     <Typography
//       fontSize={13}
//       fontWeight={700}
//       mt={2}
//       mb={2}
//       sx={{ opacity: 0.8 }}
//     >
//       {title}
//     </Typography>
//   );
// }

// function TwoFieldRow({ label, unit, defaultValue }) {
//   return (
//     <Grid container spacing={2} mb={2}>
//       <Grid item xs={8}>
//         <TextField
//           label={label}
//           size="small"
//           fullWidth
//           defaultValue={defaultValue}
//         />
//       </Grid>
//       <Grid item xs={4}>
//         <FormControl fullWidth size="small">
//           <Select defaultValue={unit}>
//             <MenuItem value="°C">°C</MenuItem>
//             <MenuItem value="°F">°F</MenuItem>
//           </Select>
//         </FormControl>
//       </Grid>
//     </Grid>
//   );
// }
